import { Box } from 'theme-ui';
import { useState, useRef, useEffect } from 'react';
import { IFeaturesBlock, IFeaturesBlockData } from '@features/cms/typescript';
import styles from '@features/cms/components/collections/FeaturesBlock/FeaturesBlock.styles';
import Heading from '@components/Heading/Heading';
import FeaturesBlockImage from '@features/cms/components/collections/FeaturesBlock/FeaturesBlockImage';
import { IParsedImage } from '@components/typescript';
import VoxboxWidget from '@features/cms/components/collections/VoxboxWidget/VoxboxWidget';
import VideoPlayer from '@components/Video/VideoPlayer';
import useIntersect from '@hooks/useIntersect';
import { useResponsive } from '@features/responsive';
import sanitizeHtml from '@utils/sanitizeHtml';
import Button from '@components/Button/Button';
type PropsType = {
  data: IFeaturesBlock<IParsedImage>;
  isFirst?: boolean;
};
const FeaturesBlock = ({
  data,
  isFirst = false
}: PropsType) => {
  const hoverRef = useRef<HTMLElement>(null);
  const {
    blocks,
    header,
    code
  } = data;
  const {
    isNarrowerThanTablet
  } = useResponsive();
  const shouldRenderImage = (block: IFeaturesBlockData<IParsedImage>) => !!block.blockImage && Object.keys(block.blockImage.image).length;
  const shouldRenderVideo = (block: IFeaturesBlockData<IParsedImage>) => !!block.blockVideo;
  const [currentVideoPlaying, setCurrentVideoPlaying] = useState(-1);
  const entry = useIntersect(hoverRef, {
    rootMargin: '-100px',
    threshold: 0.1,
    onlyOnce: true
  });
  const blockWithVideoIndices = blocks.reduce((acc, block, index) => shouldRenderVideo(block) ? [...acc, index] : acc, [] as number[]);
  useEffect(() => {
    if (entry?.isIntersecting && currentVideoPlaying === -1) setCurrentVideoPlaying(0);
  }, [entry]);
  const onVideoEnd = () => setCurrentVideoPlaying(currentVideoPlaying < blockWithVideoIndices.length - 1 ? currentVideoPlaying + 1 : 0);
  const renderMedia = (block: IFeaturesBlockData<IParsedImage>, index: number) => {
    if (shouldRenderVideo(block)) {
      return <VideoPlayer video={block.blockVideo} playVideo={blockWithVideoIndices[currentVideoPlaying] === index || currentVideoPlaying !== -1 && isNarrowerThanTablet as boolean} isEndFunc={onVideoEnd} poster={block.blockImage} loop={isNarrowerThanTablet as boolean || blockWithVideoIndices.length === 1} data-testid="features-block-video" alt={block.title} />;
    }
    if (shouldRenderImage(block)) {
      return <FeaturesBlockImage blockImage={block.blockImage} />;
    }
    return null;
  };
  const renderButton = (block: IFeaturesBlockData<IParsedImage>) => {
    if (block?.buttonURL && block?.buttonText) {
      return <div sx={styles.buttonContainer} data-testid="features-block-button-container">
          <Button variant="outlined" variantColor="brand" onClick={() => {
          window.open(block.buttonURL, '_blank')?.focus();
        }} cy={`${code}-button-${block.itemId}`} data-testid="features-block-button">
            {block.buttonText}
          </Button>
        </div>;
    }
    return null;
  };
  return <section id={code} sx={styles.section} ref={hoverRef} data-cy={code} data-testid="features-block">
      <div sx={styles.featuresContainer} data-testid="features-block-container">
        <Heading h={isFirst ? 1 : 2} addSx={styles.heading} headingType={2} pb={['16px', '16px', '24px', '24px', '40px']} mobile>
          <span dangerouslySetInnerHTML={sanitizeHtml(header || '')} data-testid="features-block-header" />
        </Heading>
        <div sx={styles.flexRow} data-testid="features-block-flex-row">
          {blocks.map((block, index) => <Box key={block.itemId} sx={styles.box} data-testid={`features-block-box-${block.itemId}`}>
              <div sx={styles.imageContainer} data-testid="features-block-image-container">
                {renderMedia(block, index)}
              </div>
              <div sx={styles.textContainer} data-testid="features-block-text-container">
                <Heading h={4} headingType={4} pb={['8px', '8px', '8px', '8px', '16px']} mobile>
                  {block.title}
                </Heading>
                <p data-testid="features-block-description">{block.description}</p>
              </div>
              {renderButton(block)}
            </Box>)}
        </div>
        {data.voxboxButton && <VoxboxWidget hoverRef={hoverRef} variant="dark" />}
      </div>
    </section>;
};
export default FeaturesBlock;